import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { ArrowRight, CalendarDays, LogOut, Mail, ShieldCheck, User } from "lucide-react";
import { api, useAuth } from "@/context/AuthContext";
import { Kicker, MaskedLine, Reveal } from "@/components/Motion";
import StatusBadge from "@/components/StatusBadge";
import { serviceBySlug } from "@/data/services";

export default function Account() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);

  useEffect(() => {
    if (!user || user === false) return;
    api
      .get("/bookings/my")
      .then((r) => setBookings(r.data.slice(0, 3)))
      .catch(() => setBookings([]));
  }, [user]);

  if (user === null) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center pt-16">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-slate-500">Loading account…</p>
      </div>
    );
  }
  if (user === false) return <Navigate to="/login" replace />;

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const memberSince = user.created_at
    ? new Date(user.created_at).toLocaleDateString("en-IN", { year: "numeric", month: "long", day: "numeric" })
    : "—";

  return (
    <div className="mx-auto max-w-7xl px-4 pb-24 pt-28 sm:px-6 lg:px-8" data-testid="account-page">
      <Kicker>Customer Account</Kicker>
      <h1 className="mt-3 font-display font-bold tracking-tight text-slate-900">
        <MaskedLine delay={0.1} className="text-3xl sm:text-5xl">
          Hello, {user.name?.split(" ")[0]}.
        </MaskedLine>
      </h1>

      <div className="mt-12 grid gap-6 lg:grid-cols-12">
        <Reveal className="lg:col-span-5">
          <div className="h-full rounded-xl border border-slate-200 bg-white p-8 shadow-sm" data-testid="account-profile-card">
            <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-500">
              Profile
            </p>
            <div className="mt-6 space-y-5">
              <div className="flex items-center gap-4">
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50 text-blue-900">
                  <User className="h-5 w-5" aria-hidden="true" />
                </span>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-slate-400">Name</p>
                  <p className="font-medium text-slate-900" data-testid="account-name">{user.name}</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50 text-blue-900">
                  <Mail className="h-5 w-5" aria-hidden="true" />
                </span>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-slate-400">Email</p>
                  <p className="font-medium text-slate-900" data-testid="account-email">{user.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50 text-blue-900">
                  <ShieldCheck className="h-5 w-5" aria-hidden="true" />
                </span>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-slate-400">Member since</p>
                  <p className="font-medium text-slate-900">{memberSince}</p>
                </div>
              </div>
            </div>
            <button
              onClick={handleLogout}
              data-testid="account-logout-button"
              className="mt-8 inline-flex items-center gap-2 rounded-md border-2 border-slate-300 px-5 py-2.5 text-sm font-medium text-slate-700 transition-colors duration-200 hover:border-red-300 hover:text-red-600"
            >
              <LogOut className="h-4 w-4" aria-hidden="true" />
              Sign out
            </button>
          </div>
        </Reveal>

        <Reveal delay={0.1} className="lg:col-span-7">
          <div className="flex h-full flex-col rounded-xl border border-slate-200 bg-white p-8 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-500">
                Recent bookings
              </p>
              <Link
                to="/my-bookings"
                data-testid="account-all-bookings-link"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-blue-900 hover:text-blue-700"
              >
                View all
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </div>

            {bookings.length === 0 ? (
              <div className="flex flex-1 flex-col items-start justify-center py-8">
                <CalendarDays className="h-8 w-8 text-slate-300" aria-hidden="true" />
                <p className="mt-4 text-sm leading-relaxed text-slate-600">
                  No bookings yet. Choose a service and book your first slot — it takes under two
                  minutes.
                </p>
                <Link
                  to="/book"
                  data-testid="account-book-button"
                  className="mt-6 inline-flex items-center gap-2 rounded-md bg-blue-900 px-5 py-2.5 text-sm font-medium text-white transition-colors duration-200 hover:bg-blue-800"
                >
                  Book a service
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Link>
              </div>
            ) : (
              <ul className="mt-5 space-y-3">
                {bookings.map((b) => (
                  <li
                    key={b.id}
                    className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-100 bg-slate-50 px-4 py-3.5"
                    data-testid={`account-booking-${b.id}`}
                  >
                    <div>
                      <p className="text-sm font-medium text-slate-900">
                        {serviceBySlug(b.service)?.title || b.service}
                      </p>
                      <p className="font-mono text-[11px] text-slate-500">
                        #{b.id.slice(-8).toUpperCase()}
                        {b.fee_paise > 0 ? ` · ₹${b.fee_paise / 100}` : " · Free"}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <StatusBadge value={b.status} />
                      <StatusBadge value={b.payment_status} />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Reveal>
      </div>
    </div>
  );
}
